import { Action } from "./action";
import { ActionBase } from "./actionBase";
import { CanceledAction } from "./canceledAction";
import { DiscontinuedAction } from "./discontinuedAction";
import { MedicationMetadata } from "./medicationMetadata";
import { OrderLevelOfCareAllOfPatientLocation } from "./orderLevelOfCareAllOfPatientLocation";
import { OrderSignatureRequest } from "./orderSignatureRequest";
import { OrderSignatureRequests } from "./orderSignatureRequests";

export interface Order {
  administeredByOther?: string;
  approvalType: Order.ApprovalTypeEnum;
  canceledAction?: CanceledAction;
  careLocation?: string;
  careLocationId?: number;
  careLocationType: string;
  careLocationTypeId?: number;
  categories?: Set<string>;
  categoryOtherDescription?: string;
  createdAction?: ActionBase;
  createdById?: number;
  createdDatetime?: string;
  diagnosis?: string;
  diagnosisDescription?: string;
  diagnosisType?: Order.DiagnosisTypeEnum;
  discipline?: string;
  discontinueDate?: string;
  discontinueDateDisplay?: string;
  discontinuedAction?: DiscontinuedAction;
  DMEType?: string;
  dosageDosage: string;
  dosageFrequency: string;
  dosageForm: string;
  dosageRoute: string;
  dosageStrength: string;
  duration: number;
  externalPatientChartId?: number;
  eSignVerification?: boolean;
  history?: Array<Action>;
  hospicePays?: boolean;
  coverageDesignation?: string;
  ingredients?: Array<string>;
  isCompound?: boolean;
  isControlledSubstance?: boolean;
  issue?: string;
  orderNotes?: string;
  levelOfCare: string;
  levelOfCareSymptoms?: Set<string>;
  levelOfCareSymptomsOther?: string;
  MAR?: boolean;
  MARDaysOfTheWeek?: Array<string>;
  MARNotes?: string;
  MARTimes?: Array<string>;
  medicationId?: string;
  migrationId?: number;
  modifiedAction?: ActionBase;
  nameDescription: string;
  ndc?: string;
  orderId?: string;
  orderingProviderId?: number;
  orderingProviderDisplayName: string;
  orderType: string;
  patientChartId?: number;
  patientName?: string;
  patientLocation?: OrderLevelOfCareAllOfPatientLocation;
  PRN?: boolean;
  PRNQuantity?: number;
  PRNReasons?: string;
  providerNotes?: string;
  readBackAndVerified?: boolean;
  reasonForRespite?: string;
  reasonsForGIP?: Array<string>;
  reasonsForGIPOther?: string;
  rxNormal?: string;
  signatureRequests?: OrderSignatureRequests;
  startDate: string;
  startDateDisplay?: string;
  symptoms?: string;
  teamId?: number;
  teamName?: string;
  teamQbosId?: number;
  tenantId?: number;
  timeInterval: Order.TimeIntervalEnum;
  durationInterval: Order.TimeIntervalEnum;
  updatedById?: number;
  updatedDatetime?: string;
  visits: number;
  medicationMetadata?: MedicationMetadata;
  postActions?: Action[];
  declined?: boolean;
  declinedDate?: string;
  declinedReason?: string;
  otherDiscipline?: string;
  orderInstructions?: string;
  prescriptionDrugFormat?: string;
  nonERXAdministrationFormat?: string;
  orderSubtype?: string;
}

export namespace Order {
  export type ApprovalTypeEnum =
    | "verbal"
    | "written"
    | "noSignatureRequired"
    | "pending";
  export const ApprovalTypeEnum = {
    Verbal: "verbal" as ApprovalTypeEnum,
    Written: "written" as ApprovalTypeEnum,
    NoSignatureRequired: "noSignatureRequired" as ApprovalTypeEnum,
    Pending: "pending" as ApprovalTypeEnum,
  };
  export type DiagnosisTypeEnum = "primary" | "secondary" | "related";
  export const DiagnosisTypeEnum = {
    Primary: "primary" as DiagnosisTypeEnum,
    Secondary: "secondary" as DiagnosisTypeEnum,
    Related: "related" as DiagnosisTypeEnum,
  };
  export type TimeIntervalEnum = "DAY" | "WEEK" | "2_WEEKS" | "MONTH";
  export const TimeIntervalEnum = {
    Day: "DAY" as TimeIntervalEnum,
    Week: "WEEK" as TimeIntervalEnum,
    Month: "MONTH" as TimeIntervalEnum,
  };
  export function isOrder(order: Partial<Order>): order is Order {
    return getMissingFields(order).length === 0;
  }
  export function getMissingFields(order: Partial<Order>): Array<string> {
    return [
      "orderType",
      "startDate",
      "orderingProviderDisplayName",
      "approvalType",
    ].filter((field) => !order[field]);
  }
  export function isDiscontinued(order: Order): boolean {
    if (!order.discontinueDate) {
      return false;
    }
    return Date.parse(order.discontinueDate) <= Date.now().valueOf();
  }

  /**
   * CO-3363: `signatureRequests` is optional on the model and orders reach the
   * UI straight from OpenSearch, so a row without it is a shape the caller must
   * survive. This dereferenced it unguarded, and its only caller
   * (ProviderPanelPage) runs it inside the reduce that builds the whole grid —
   * so one malformed row emptied the entire signing queue instead of being
   * skipped. An order with no created signature request is simply not signable.
   */
  export function isCreatedOrderSignable(
    val: Order,
    orderingProviderId: number
  ): boolean {
    if (!val || !val.signatureRequests || !val.signatureRequests.created) {
      return false;
    }
    return (
      val.signatureRequests.created.signedStatus ===
        OrderSignatureRequest.SignedStatusEnum.No &&
      val.approvalType === Order.ApprovalTypeEnum.Verbal &&
      val.orderingProviderId === orderingProviderId
    );
  }

  export function isDiscontinuedOrderSignable(
    val: Order,
    orderingProviderId: number
  ): boolean {
    // CO-3363: guarded for the same reason as isCreatedOrderSignable above —
    // and `discontinuedAction` is equally optional, so an order carrying a
    // discontinued signature request but no discontinued action threw here too.
    if (
      !val ||
      !val.signatureRequests ||
      !val.signatureRequests.discontinued ||
      !val.discontinuedAction
    ) {
      return false;
    }
    return (
      val.signatureRequests.discontinued.signedStatus ===
        OrderSignatureRequest.SignedStatusEnum.No &&
      val.discontinuedAction.approvalType === Order.ApprovalTypeEnum.Verbal &&
      val.discontinuedAction.orderingProviderId === orderingProviderId
    );
  }

  export function isVisitFrequencyDeclined(order: Order) {
    return order.orderType === "VISIT_FREQUENCY" && order.declined;
  }
}



// WEBPACK FOOTER //
// ./src/models/order-management/order.ts